import { Component, computed, effect, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormField, email, form, maxLength, minLength, required, schema } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { of } from 'rxjs';
import { MceApiService, TokenDto, UserUpdateEmailDto } from '../../../core/mceapi.client.generated';
import { MceSaveCancelComponent, MceSaveCancelConfig, MceSaveCancelEvent } from '../../components/mce-save-cancel/mce-save-cancel.component';
import { InfoMessageComponent } from '../../components/info-message/info-message.component';

export interface ChangeEmailModel {
  acknowledged: boolean;
  newEmail: string;
  otpToken: string;
  otp: string;
}

const changeEmailSchema = schema<ChangeEmailModel>((path) => {
  required(path.newEmail, { message: 'New email is required' });
  email(path.newEmail, { message: 'Enter a valid email address' });

  required(path.otpToken);
  minLength(path.otpToken, 6);

  required(path.otp, { message: 'One time password is required' });
  minLength(path.otp, 6, { message: 'One time password must be at least 6 characters' });
  maxLength(path.otp, 6, { message: 'One time password must be 6 characters' });
});

@Component({
  selector: 'mce-change-email-dialog',
  imports: [
    FormField,
    MceSaveCancelComponent,
    InfoMessageComponent,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
  ],
  templateUrl: './change-email.component.html'
})
export class ChangeEmailComponent {
  readonly dialogRef = inject(MatDialogRef<ChangeEmailComponent>);

  private readonly mceApiService = inject(MceApiService);

  readonly formModel = signal<ChangeEmailModel>({
    acknowledged: false,
    newEmail: '',
    otpToken: '',
    otp: ''
  });
  readonly form = form<ChangeEmailModel>(this.formModel, changeEmailSchema);

  readonly otpRequest = rxResource<TokenDto | undefined, { requestOtp: boolean }>({
    params: () => ({
      requestOtp: this.form.acknowledged().value() && this.form.newEmail().valid() && this.form.otpToken().value().length === 0
    }),
    stream: ({ params }) => params.requestOtp ? this.mceApiService.v1UsersOtpEmailProvided({ email: this.form.newEmail().value() }) : of(undefined),
  });

  readonly requestOtpEffect = effect(() => {
    const token = this.otpRequest.value()?.token ?? this.form.otpToken().value();
    this.form.otpToken().value.set(token);
  });

  readonly viewState = computed(() => ({
    enableChange: this.form.acknowledged().value() && this.form.newEmail().valid() && this.form().valid(),
    showOtpInput: this.form.otpToken().valid(),
    showOtpError: this.otpRequest.error() !== undefined,
    otpSending: this.otpRequest.isLoading(),
  }));

  protected readonly saveCancelConfig = computed<MceSaveCancelConfig>(() => ({
    cancel: { show: true, enable: true }
  }));

  onChangeEmailClick(): void {
    const result: UserUpdateEmailDto = {
      newEmail: this.form.newEmail().value(),
      otpToken: this.form.otpToken().value(),
      otp: this.form.otp().value()
    };

    this.dialogRef.close(result);
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  onSaveCancelAction(event: MceSaveCancelEvent): void {
    if (event.type === 'cancel') {
      this.onCancelClick();
    }
  }
}
